import type { NextPage } from 'next'
import Image from 'next/image'
import { useEffect, useRef } from 'react'
import Grid from '../../components/grid/Grid'
import CustomMetaData from '../../components/general/customMetaData/CustomMetaData'
import { GenericPageLayoutProps } from '../../components/general/genericPageLayout/GenericPageLayout'
import CustomImage from '../../components/customImage/CustomImage'

import styles from '../../styles/pages/Company.module.scss'

import { useGlobalContentContext } from '../../context/GlobalContentContext'

import { parseGenericPage } from '../../utils/parsers'
import { getAPI, createMarkup } from '../../utils/helpers'

type Props = {} & GenericPageLayoutProps

const Company = (props: Props) => {
  const { setData } = useGlobalContentContext()

  useEffect(() => {
    setData(props.globalData)
  }, [props.globalData, setData])

  return (
    <div className={styles.container}>
      <CustomMetaData
        title={props.originalResponse.meta_title}
        description={props.originalResponse.meta_description}
        image={props.originalResponse.meta_image}
      />

      <div className={styles.main}>
        <section className={styles.hero}>
          <h1 className={styles.heroTitle}>{props.originalResponse.title}</h1>

          <div className={styles.heroImage}>
            <CustomImage
              src={props.originalResponse.hero}
              layout="fill"
              objectFit="cover"
            />
          </div>

          <div
            className={styles.heroDetail}
            dangerouslySetInnerHTML={createMarkup(
              props.originalResponse.detail
            )}
          ></div>
        </section>

        <section className={styles.missionSection}>
          <div className={styles.maxWidthContainer}>
            <h2 className={styles.missionTitle}>
              {props.originalResponse.missionsection.title}
            </h2>
            <div
              className={styles.missionDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.missionsection.detail
              )}
            ></div>
          </div>
        </section>

        <section id="history" className={styles.timelineSection}>
          <h2 className={styles.timelineSectionTitle}>Timeline</h2>
          {props.originalResponse.timelines.map(
            (timeline: any, index: number) => (
              <div key={index} className={styles.timeline}>
                <h3 className={styles.timelineYear}>{timeline.year}</h3>
                <div
                  className={styles.timelineDetail}
                  dangerouslySetInnerHTML={createMarkup(timeline.detail)}
                ></div>
              </div>
            )
          )}
        </section>

        <section id="leadership" className={styles.leadershipSection}>
            <h2 className={styles.leadershipTitle}>Senior Leadership</h2>

            <div className={styles.leadershipItems}>
              <Grid columns={4}>
                {props.originalResponse.leadership_items.map(
                  (item: any, index: number) => (
                    <div key={index} className={styles.leadershipItem}>
                      <CustomImage src={item} layout="responsive" />
                      <div className={styles.leadershipItemName}>
                        {item.name}
                      </div>
                      <div className={styles.leadershipItemDetail}>
                        {item.detail}
                      </div>

                      {item.linkedin && (
                        <div className={styles.leadershipItemLinkedin}>
                          <a
                            href={item.linkedin}
                            target="_blank"
                            rel="noreferrer"
                          >
                            LINKEDIN
                          </a>
                        </div>
                      )}
                    </div>
                  )
                )}
              </Grid>
            </div>
          </section>

        <section id="directors" className={styles.directorsSection}>
            <h2 className={styles.directorsTitle}>Board of Directors</h2>

            <div className={styles.directorItems}>
              <Grid columns={4}>
                {props.originalResponse.director_items.map(
                  (item: any, index: number) => (
                    <div key={index} className={styles.directorItem}>
                      <div className={styles.directorItemName}>{item.name}</div>
                      <div className={styles.directorItemDetail}>
                        {item.detail}
                      </div>

                      {item.linkedin && (
                        <div className={styles.directorItemLinkedin}>
                          <a
                            href={item.linkedin}
                            target="_blank"
                            rel="noreferrer"
                          >
                            LINKEDIN
                          </a>
                        </div>
                      )}
                    </div>
                  )
                )}
              </Grid>
            </div>
          </section>

        <section id="partners" className={styles.partnersSection}>
          <div className={styles.maxWidthContainer}>
            <h2 className={styles.partnersTitle}>
              {props.originalResponse.partnerssection.title}
            </h2>
            <div
              className={styles.partnersDetail}
              dangerouslySetInnerHTML={createMarkup(
                props.originalResponse.partnerssection.detail
              )}
            ></div>

            <div className={styles.partners}>
              <Grid columns={4}>
                {props.originalResponse.partners.map(
                  (partner: any, index: number) => (
                    <div key={index} className={styles.partnerItem}>
                      <CustomImage src={partner} layout="responsive" />
                    </div>
                  )
                )}
              </Grid>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export async function getStaticProps() {
  const page: GenericPageLayoutProps = await parseGenericPage(
    `${getAPI()}/aboutpage/`
  )
  return {
    props: {
      originalResponse: page.originalResponse,
      globalData: page.globalData,
      title: page.title,
    },
    revalidate: NEXTJS_REVALIDATE_PAGE_IN_SECONDS,
  }
}

export const NEXTJS_REVALIDATE_PAGE_IN_SECONDS = 10
export default Company
